'use client'
import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Badge } from '~/components/ui/badge'
import { urlThumbnail } from '~/lib/helper'

interface Product {
    id: string
    name: string
    price?: number
    originalPrice?: number
    image?: string
    link?: string
    discount?: number
    installment?: string
    note?: string
}

interface ProductsCardProps {
    ids?: string
    products?: Product[]
    children?: React.ReactNode
}

const productsExample = [
    {
        id: '1',
        name: 'OPPO Reno11 F 5G 8GB 256GB - Đã Kích Hoạt',
        price: 4990000,
        originalPrice: 8990000,
        discount: 44,
        installment: 'Trả góp 0%',
        note: 'S-Student giảm thêm 499.000₫',
        image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
        link: 'https://www.google.com'
    },
    {
        id: '2',
        name: 'Sản phẩm 2',
        price: 2000000,
        originalPrice: 3000000,
        discount: 33,
        installment: 'Trả góp 0%',
        image: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
        link: 'https://www.google.com'
    },
]

const ProductsCard = ({ ids, products: productsFromProps, children }: ProductsCardProps) => {
    let products: Product[] = []
    if (productsFromProps && productsFromProps.length > 0) {
        products = productsFromProps
    } else if (ids) {
        const productIds = ids
            .split(/[,\s]+/)
            .map(id => id.trim())
            .filter(id => id.length > 0)
        products = productsExample.filter(product => productIds.includes(product.id))
    }

    if (products.length === 0) {
        return (
            <div className='my-4'>
                <div className='bg-white dark:bg-zinc-950 rounded-lg shadow-md border border-gray-200 dark:border-zinc-800 p-4'>
                    <p className='text-sm text-gray-500 dark:text-gray-400 mb-2'>
                        {ids ? `Danh sách sản phẩm: ${ids}` : 'Không có sản phẩm nào'}
                    </p>
                    {children && <div className='mt-2'>{children}</div>}
                </div>
            </div>
        )
    }

    return (
        <div className='my-6 grid grid-cols-1 min-[340px]:grid-cols-2 sm:grid-cols-3 gap-4 not-prose'>
            {products.map((product) => {
                const calculatedDiscount = product.discount || (product.originalPrice && product.price 
                    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
                    : 0)
                return (
                    <div
                        key={product.id}
                        className='bg-white dark:bg-zinc-900 rounded-2xl shadow-md border border-gray-200 dark:border-zinc-800 overflow-hidden relative hover:shadow-lg transition-all duration-300 hover:border-second'
                    >
                        <Link href={product.link || '#'} className='block no-underline'>
                            <div className='absolute top-1 left-1 right-1 z-10 flex items-center flex-wrap justify-between gap-y-0.5'>
                                {calculatedDiscount > 0 && (
                                    <Badge className='bg-red-600! text-white! font-semibold rounded-full border-0 py-1.5'>
                                        Giảm {calculatedDiscount}%
                                    </Badge>
                                )}
                                {product.installment && (
                                    <Badge variant='secondary' className='bg-blue-600! text-white! rounded-full border-0 py-1.5'>
                                        {product.installment}
                                    </Badge>
                                )}
                            </div>

                            {product.image && (
                                <div className='relative w-full aspect-square bg-white dark:bg-zinc-900 overflow-hidden'>
                                    <Image
                                        src={urlThumbnail(product.image)}
                                        alt={product.name}
                                        fill
                                        className='object-cover p-0 m-0 rounded-none'
                                    />
                                </div>
                            )}

                            <div className='p-3 space-y-1 bg-white dark:bg-zinc-900'>
                                <h3 className='font-semibold my-0 no-underline text-sm text-gray-800 dark:text-gray-200 line-clamp-2'>
                                    {product.name}
                                </h3>
                                <div className='flex items-baseline flex-wrap gap-x-2'>
                                    <span className='text-base font-bold text-red-600 dark:text-red-500'>
                                        {product.price?.toLocaleString('vi-VN')}₫
                                    </span>
                                    {product.originalPrice && (
                                        <span className='text-xs text-gray-400 font-normal dark:text-gray-500 line-through'>
                                            {product.originalPrice.toLocaleString('vi-VN')}₫
                                        </span>
                                    )}
                                </div>

                                {product.note && (
                                    <Badge variant='secondary' className='bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-3 py-1.5 rounded-lg text-xs font-medium border-0 w-fit'>
                                        {product.note}
                                    </Badge>
                                )}

                                {children && (
                                    <div className='text-xs text-gray-600 dark:text-gray-400'>
                                        {children}
                                    </div>
                                )}
                            </div>
                        </Link>
                    </div>
                )
            })}
            {children && (
                <div className='col-span-full mt-6'>
                    {children}
                </div>
            )}
        </div>
    )
}

export type { Product }
export default ProductsCard

